import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '../lib/utils';

const badgeVariants = cva(
  cn(
    'inline-flex items-center rounded-md px-2.5 py-1 text-sm font-normal transition-colors',
    'focus:ring-ring focus:ring-2 focus:ring-offset-2 focus:outline-hidden',
    'whitespace-nowrap',
  ),
  {
    variants: {
      variant: {
        primary: 'bg-brand-400 text-white dark:text-brand-100 dark:bg-brand-300',
        secondary: 'bg-steel-50 text-steel-400 dark:text-steel-200 dark:bg-brand-400',
        tertiary: 'border border-peach-500 bg-transparent text-fire-500',
        success: 'bg-grass-100 text-grass-500 dark:bg-[#104731] dark:text-grass-300',
        danger: 'bg-[#fff6f8] text-fire-500 dark:bg-[#341A29] dark:text-fire-400',
        'danger-outline': 'border border-fire-500 bg-fire-2 text-fire-500',
        'danger-inverted': 'bg-fire-500 text-white dark:bg-[#341A29] dark:text-fire-400',
        info: 'bg-brand-100 text-brand-400 dark:bg-[#142546] dark:text-brand-200',
        'info-outline': 'border border-brand-400 bg-brand-100 text-brand-400',
        upcoming: 'bg-fire-100 text-peach-600',
        warning: 'bg-glitter-200 text-glitter-500 dark:bg-[#5A410F] dark:text-peach-500',
        glitter: 'bg-glitter-100 text-steel-500',
      },
      smallCaps: {
        true: 'text-xs font-semibold uppercase tracking-wide',
      },
    },
    defaultVariants: {
      variant: 'primary',
    },
  },
);

export type BadgeVariants = VariantProps<typeof badgeVariants>;
export interface BadgeProps extends React.HTMLAttributes<HTMLDivElement>, BadgeVariants {}

function Badge({ className, variant, smallCaps = false, ...props }: BadgeProps) {
  return <div className={cn(badgeVariants({ variant, smallCaps }), className)} {...props} />;
}

export { Badge, badgeVariants };
